import {
	Dialog,
	DialogContent,
	DialogActions,
	Paper,
	Typography,
	Grid,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	TableContainer,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Button,
	TextField,
	InputAdornment,
	Box,
	DialogTitle,
} from "@mui/material";
import { useState, useMemo } from "react";
import { standardJobDocumentTemplates } from "./standard-job-document-templates";
import SearchIcon from "@mui/icons-material/Search";

interface StandardTemplatesDialogProps {
	open: boolean;
	onClose: () => void;
	templates: any[];
	onCreateFromStandardTemplate: (templateCode: string) => void;
}

export const StandardTemplatesDialog = ({
	open,
	onClose,
	templates,
	onCreateFromStandardTemplate,
}: StandardTemplatesDialogProps) => {
	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
		null
	);
	const [searchQuery, setSearchQuery] = useState("");

	const filteredTemplates = useMemo(() => {
		if (!searchQuery.trim()) return templates;

		const query = searchQuery.toLowerCase();
		return templates.filter(
			(template) =>
				template.name.toLowerCase().includes(query) ||
				template.code.toLowerCase().includes(query) ||
				template.job_items.some(
					(item: any) =>
						item.code_e.toLowerCase().includes(query) ||
						item.description.toLowerCase().includes(query)
				)
		);
	}, [templates, searchQuery]);

	const selectedTemplate = useMemo(() => {
		if (!selectedTemplateId) return null;
		return standardJobDocumentTemplates.find(
			(t) => t.code === selectedTemplateId
		);
	}, [selectedTemplateId]);

	const handleClose = () => {
		setSelectedTemplateId(null);
		onClose();
	};

	const handleTakeAllTemplates = () => {
		templates.forEach((template) => {
			onCreateFromStandardTemplate(template.code);
		});
		handleClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="xl"
			PaperProps={{
				style: {
					borderRadius: "0.75rem",
					padding: "1rem",
					boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
					height: "90vh",
					width: "90vw",
					maxWidth: "1400px",
				},
			}}
			fullWidth
		>
			<DialogTitle>
				<Typography variant="h5" component="h2">
					Neue Vorlage aus Standardfällen
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Wählen Sie eine Vorlage aus den Anwendungsfällen des VZLS
					aus und kopieren Sie die Positionen in Ihre Vorlage.
				</Typography>
			</DialogTitle>
			<DialogContent>
				<TextField
					fullWidth
					placeholder="Nach Vorlagen suchen..."
					variant="outlined"
					size="small"
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					sx={{ mb: 2 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon />
							</InputAdornment>
						),
					}}
				/>

				<div className="flex flex-row gap-4 h-[90%]">
					<div className="w-1/4">
						<Paper
							elevation={0}
							sx={{
								height: "100%",
								overflow: "auto",
								border: "1px solid rgba(0,0,0,0.1)",
								borderRadius: "0.5rem",
							}}
						>
							<List>
								{filteredTemplates.map((template) => (
									<ListItem
										disablePadding
										key={template.code}
										sx={{
											"&:hover": {
												backgroundColor:
													"rgba(0,0,0,0.04)",
											},
										}}
									>
										<ListItemButton
											selected={
												selectedTemplateId ===
												template.code
											}
											onClick={() =>
												setSelectedTemplateId(
													template.code
												)
											}
										>
											<ListItemText
												primary={template.name}
												secondary={
													<>
														<Typography
															variant="body2"
															component="span"
															color="text.secondary"
														>
															{template.code}
														</Typography>
														<div className="mt-1">
															<Typography
																variant="body2"
																color="text.secondary"
																sx={{
																	fontStyle:
																		"italic",
																}}
															>
																{
																	template
																		.job_items
																		.length
																}{" "}
																Positionen
															</Typography>
														</div>
													</>
												}
											/>
										</ListItemButton>
									</ListItem>
								))}
							</List>
						</Paper>
					</div>
					<div className="w-3/4">
						<Paper
							elevation={0}
							sx={{
								height: "100%",
								overflow: "auto",
								border: "1px solid rgba(0,0,0,0.1)",
								borderRadius: "0.5rem",
								padding: 2,
								display: "flex",
								flexDirection: "column",
							}}
						>
							{selectedTemplate ? (
								<>
									<Typography variant="h5" gutterBottom>
										{selectedTemplate.name} (
										{selectedTemplate.code})
									</Typography>
									<TableContainer sx={{ flexGrow: 1 }}>
										<Table size="small" stickyHeader>
											<TableHead>
												<TableRow
													sx={{ "& th": { py: 0.2 } }}
												>
													<TableCell>Code</TableCell>
													<TableCell>
														Beschreibung
													</TableCell>
													<TableCell align="right">
														Menge
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{selectedTemplate.job_items.map(
													(item) => (
														<TableRow
															key={item.code_e}
															hover
															sx={{
																"& td": {
																	py: 0.2,
																},
															}}
														>
															<TableCell>
																{item.code_e}
															</TableCell>
															<TableCell>
																{
																	item.description
																}
															</TableCell>
															<TableCell align="right">
																{item.quantity}
															</TableCell>
														</TableRow>
													)
												)}
											</TableBody>
										</Table>
									</TableContainer>
								</>
							) : (
								<div className="flex h-full items-center justify-center">
									<Typography
										variant="h6"
										color="textSecondary"
									>
										Wählen Sie eine Vorlage aus der Liste
										aus
									</Typography>
								</div>
							)}
						</Paper>
					</div>
				</div>
			</DialogContent>
			<DialogActions
				sx={{
					padding: "16px 24px",
					display: "flex",
					justifyContent: "space-between",
				}}
			>
				<Button onClick={handleClose}>Abbrechen</Button>
				<div className="flex gap-2">
					<Button
						onClick={handleTakeAllTemplates}
						variant="outlined"
						color="primary"
					>
						Alle Vorlagen übernehmen
					</Button>
				{selectedTemplate && (
					<Button
						onClick={() => {
							onCreateFromStandardTemplate(selectedTemplate.code);
							handleClose();
						}}
						variant="contained"
						color="primary"
					>
						Diese Vorlage übernehmen
					</Button>
				)}
				</div>
			</DialogActions>
		</Dialog>
	);
};
